import { SectionHeaderWithAccent } from '@/components/display/SectionHeaderWithAccent/SectionHeaderWithAccent';
import { useDispositiveDiagramSyncContext } from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.context';
import { isBeneficiaryDesignationEntityKind } from '@/modules/dispositiveProvisions/dispositiveProvisions.label';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { useEditEntitySplitScreenModalContext } from '@/modules/entities/EditEntitySplitScreen/contexts/editEntitySplitScreenModal.context';
import { getTileEntityMetadata } from '@/modules/entityDiagram/getTileEntityMetadata';

export function EditEntitySplitScreenHeader() {
  const { headerRef, isDPSection, isBDGSection } =
    useEditEntitySplitScreenModalContext();
  const title = useSplitScreenHeaderTitle();

  // DP/BDG: the form body remounts on every diagram tile click and portals its
  // header controls into `headerRef`. Render a stable header bar here — title
  // derived from context — so the header doesn't blink out while the newly
  // selected target loads. The section switcher / death-order selector fade
  // into the slot once the new form mounts.
  if (isDPSection || isBDGSection) {
    return (
      <SectionHeaderWithAccent
        containerProps={{ pt: 1.5, pb: 1.5, pl: 2, pr: 2 }}
        header={{ heading: title }}
        rightHeaderContent={
          <div
            ref={headerRef}
            // Reserve the control-row height (the form portals ~40px `sm`
            // buttons in) so the header keeps the same height in the loading
            // and loaded states instead of growing when controls mount.
            style={{ display: 'flex', alignItems: 'center', minHeight: 40 }}
          />
        }
      />
    );
  }

  // Entity-form sections portal a full header (title + controls) into this div
  // and don't remount on section switch, so they don't blink — leave as-is.
  return <div ref={headerRef} />;
}

/**
 * Title for the stable DP/BDG header shell, matching what the form body would
 * render: a BDG-backed target shows its own display name; a DP target keeps the
 * root entity as the anchor. Mirrors the body routing in
 * {@link NonRootTargetSwitchedBody} (same BDG-kind helpers) so the title
 * doesn't drift from the body that ultimately mounts.
 */
function useSplitScreenHeaderTitle(): string {
  const { entityId, displayName: rootDisplayName } = useEntityDetailsContext();
  const { committedTargetNode } = useDispositiveDiagramSyncContext();

  const isOnRootTarget =
    !committedTargetNode || committedTargetNode.pulid === entityId;
  if (isOnRootTarget) {
    return rootDisplayName ?? '';
  }

  const meta = getTileEntityMetadata(committedTargetNode?.node ?? null);
  const rendersBdgBody = isBeneficiaryDesignationEntityKind(meta.entityKind);
  if (rendersBdgBody) {
    return meta.displayName ?? rootDisplayName ?? '';
  }
  return rootDisplayName ?? '';
}
